// Client-side helpers for fetching volume data directly from Azure Blob Storage
// (trading-data-v2/volume/), using the same container-level read+list SAS token as
// stockMetadataBlob.ts (GET /api/watchlist/sas-token). Replaces the previous
// GET /api/accumulation/data/:type round-trips. volume.zip is generated by
// processor/src/scheduler_task/2h_volume.rs; broker_volume/{BROKER}.zip by
// processor/src/scheduler_task/2h_broker_volume.rs.

import { api } from '../services/api';
import { fetchZipEntry } from './zipRangeReader';

const VOL_ROOT = 'volume/';

export type VolumeType = 'all' | 'foreign' | 'domestic';

export interface VolumeResult {
  type: VolumeType;
  accumulationData: Record<string, unknown>[];
  total: number;
}

function buildBlobUrl(path: string, containerUrl: string, sasToken: string): string {
  const isDev = (import.meta as any).env?.DEV || false;
  return isDev
    ? `/azure-blob-proxy/trading-data-v2/${path}?${sasToken}`
    : `${containerUrl}/${path}?${sasToken}`;
}

function parseVolumeCSV(csvText: string, type: VolumeType): VolumeResult {
  const lines = csvText.split('\n');
  if (lines.length === 0) return { type, accumulationData: [], total: 0 };

  const firstLine = lines[0];
  if (!firstLine) return { type, accumulationData: [], total: 0 };
  const headers = firstLine.split(',').map(h => h.trim());
  const accumulationData: Record<string, unknown>[] = [];

  for (let i = 1; i < lines.length; i++) {
    const rawLine = lines[i];
    if (!rawLine) continue;
    const line = rawLine.trim();
    if (!line) continue;
    const values = line.split(',');

    const obj: Record<string, unknown> = {};
    headers.forEach((col, idx) => {
      const v = values[idx];
      if (col === 'Stock') {
        obj['Ticker'] = String(v ?? '');
      } else {
        obj[col] = Number(v) || 0;
      }
    });
    accumulationData.push(obj);
  }

  return { type, accumulationData, total: accumulationData.length };
}

/**
 * Fetch a file from volume/volume.zip directly from Azure Blob.
 */
export async function fetchVolumeData(
  type: VolumeType,
  part: 1 | 2 = 1,
): Promise<{ success: true; data: VolumeResult }> {
  try {
    const tokenRes = await api.getStockMetadataSasToken();
    if (!tokenRes.success || !tokenRes.data) throw new Error(tokenRes.error);
    const { containerUrl, sasToken } = tokenRes.data;
    const zipName = part === 2 ? 'volume_part2.zip' : 'volume.zip';
    const zipUrl = buildBlobUrl(`${VOL_ROOT}${zipName}`, containerUrl, sasToken);

    const buffer = await fetchZipEntry(zipUrl, `volume_${type}.csv`);
    if (!buffer) return { success: true, data: { type, accumulationData: [], total: 0 } };

    const text = new TextDecoder().decode(buffer);
    return { success: true, data: parseVolumeCSV(text, type) };
  } catch (err: any) {
    console.error(`Error fetching volume part ${part} data:`, err);
    return { success: true, data: { type, accumulationData: [], total: 0 } };
  }
}

/**
 * Fetch a file from volume/broker_volume/{BROKER}.zip directly from Azure Blob.
 * Broker-specific buy volume per identity, generated by
 * processor/src/scheduler_task/2h_broker_volume.rs.
 * When broker is "All" this should not be called - use fetchVolumeData instead.
 */
export async function fetchBrokerVolumeData(
  broker: string,
  type: VolumeType,
  part: 1 | 2 = 1,
): Promise<{ success: true; data: VolumeResult }> {
  try {
    const tokenRes = await api.getStockMetadataSasToken();
    if (!tokenRes.success || !tokenRes.data) throw new Error(tokenRes.error);
    const { containerUrl, sasToken } = tokenRes.data;
    const base = broker.toUpperCase();
    const zipName = part === 2 ? `${base}_part2.zip` : `${base}.zip`;
    const zipUrl = buildBlobUrl(`${VOL_ROOT}broker_volume/${zipName}`, containerUrl, sasToken);
    const buffer = await fetchZipEntry(zipUrl, `volume_${type}.csv`);
    if (!buffer) return { success: true, data: { type, accumulationData: [], total: 0 } };
    const text = new TextDecoder().decode(buffer);
    return { success: true, data: parseVolumeCSV(text, type) };
  } catch (err: any) {
    console.error(`Error fetching broker volume ${broker} part ${part}:`, err);
    return { success: true, data: { type, accumulationData: [], total: 0 } };
  }
}

/**
 * Fetch volume_dates.json from volume/volume.zip directly from Azure Blob.
 */
export async function fetchVolumeDates(): Promise<{ success: true; data: { dates: string[]; total: number } }> {
  try {
    const tokenRes = await api.getStockMetadataSasToken();
    if (!tokenRes.success || !tokenRes.data) throw new Error(tokenRes.error);
    const { containerUrl, sasToken } = tokenRes.data;
    const zipUrl = buildBlobUrl(`${VOL_ROOT}volume.zip`, containerUrl, sasToken);

    const buffer = await fetchZipEntry(zipUrl, 'volume_dates.json');
    if (!buffer) return { success: true, data: { dates: [], total: 0 } };

    const text = new TextDecoder().decode(buffer);
    const dates: string[] = text ? (JSON.parse(text)?.dates ?? []) : [];
    return { success: true, data: { dates, total: dates.length } };
  } catch (err: any) {
    console.error('Error fetching volume dates:', err);
    return { success: true, data: { dates: [], total: 0 } };
  }
}
